import React, { useState } from "react";
import {
  Catalog,
  LANGUAGE_APPS,
  LanguageApp,
  languagesAPI,
} from "./api";
import { useDeleteLanguage, useGetLanguages } from "./hooks";
import AddLanguageModal from "./AddLanguageModal";
import { toast } from "react-toastify";
import { useT } from "../../i18n";

const APP_LABEL: Record<LanguageApp, string> = {
  admin: "language.apps.admin",
  partner: "language.apps.partner",
};

const download = (filename: string, catalog: Catalog) => {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(catalog, null, 2)], { type: "application/json" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const LanguagePage: React.FC = () => {
  const t = useT();
  const { data: languages, isLoading } = useGetLanguages();
  const deleteLanguage = useDeleteLanguage();

  // null = closed, {} = add, { code, name } = edit that language's files.
  const [modal, setModal] = useState<{ code?: string; name?: string } | null>(null);
  const [search, setSearch] = useState("");

  const handleDownload = async (app: LanguageApp, code: string) => {
    try {
      download(`${app}.${code}.json`, await languagesAPI.getCatalog(app, code));
    } catch {
      toast.error(t("language.toast.downloadFailed"));
    }
  };

  const handleDelete = (code: string, name: string) => {
    if (window.confirm(t("language.confirmDelete", { name }))) {
      deleteLanguage.mutate(code);
    }
  };

  const all = Object.entries(languages || {});
  const q = search.trim().toLowerCase();
  const entries = q
    ? all.filter(([code, name]) => `${code} ${name}`.toLowerCase().includes(q))
    : all;

  const th = "p-4 font-semibold uppercase tracking-wider";

  return (
    <div className="flex flex-col flex-1 min-h-screen bg-gray-50 w-full h-full p-0 m-0">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:h-20 px-4 sm:px-8 py-4 sm:py-0 bg-white border-b border-gray-200 w-full">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{t("language.title")}</h2>
          <p className="text-xs sm:text-sm text-gray-500">{t("language.subtitle")}</p>
        </div>
        <button
          onClick={() => setModal({})}
          className="w-full sm:w-auto justify-center px-5 py-2.5 flex items-center bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium shadow-sm"
        >
          <i className="fa-solid fa-plus mr-2"></i> {t("language.addLanguage")}
        </button>
      </header>

      <main className="flex-1 p-4 sm:p-8 w-full flex flex-col gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50/50">
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                placeholder={t("language.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition"
              />
              <i className="fa-solid fa-search text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <i className="fa-solid fa-language text-gray-300 text-6xl mb-6"></i>
                <p className="text-xl font-medium text-gray-600 mb-3">
                  {q ? t("language.empty.noResultsTitle") : t("language.empty.title")}
                </p>
                <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                  {q ? t("language.empty.noResultsBody") : t("language.empty.body")}
                </p>
                {!q && (
                  <button
                    onClick={() => setModal({})}
                    className="mt-6 px-6 py-3 bg-[#111827] text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
                  >
                    <i className="fa-solid fa-plus mr-2"></i>
                    {t("language.addLanguage")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th scope="col" className={th}>{t("language.table.language")}</th>
                    {LANGUAGE_APPS.map((app) => (
                      <th key={app} scope="col" className={th}>{t(APP_LABEL[app])}</th>
                    ))}
                    <th scope="col" className={`${th} text-right`}>{t("language.table.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.map(([code, name]) => {
                    const isBase = code === "it";
                    const deleting =
                      deleteLanguage.isPending && deleteLanguage.variables === code;
                    return (
                      <tr
                        key={code}
                        className={`transition-opacity ${
                          deleting ? "opacity-50 pointer-events-none" : "hover:bg-gray-50/50"
                        }`}
                      >
                        <td className="p-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center mr-3 text-xs font-bold uppercase text-gray-600">
                              {code.slice(0, 2)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 flex items-center gap-2">
                                {name}
                                {isBase && (
                                  <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full text-blue-800 bg-blue-100">
                                    {t("language.base")}
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-500 text-xs font-mono">{code}</div>
                            </div>
                          </div>
                        </td>
                        {LANGUAGE_APPS.map((app) => (
                          <td key={app} className="p-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDownload(app, code)}
                              title={t("language.downloadJson")}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <i className="fa-regular fa-file-code text-gray-400"></i>
                              <span className="font-mono text-xs">{app}.{code}.json</span>
                              <i className="fa-solid fa-download text-xs text-gray-400"></i>
                            </button>
                          </td>
                        ))}
                        <td className="p-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => setModal({ code, name })}
                              aria-label={t("common.edit")}
                              title={t("language.uploadJson")}
                              className="text-blue-600 hover:text-blue-800 p-2"
                            >
                              <i className="fa-solid fa-edit text-sm"></i>
                            </button>
                            {!isBase && (
                              <button
                                onClick={() => handleDelete(code, name)}
                                disabled={deleteLanguage.isPending}
                                aria-label={t("language.deleteNamed", { name })}
                                title={t("language.deleteLanguage")}
                                className="text-red-600 hover:text-red-800 p-2 disabled:opacity-50"
                              >
                                <i className={`fa-solid text-sm ${deleting ? "fa-spinner fa-spin" : "fa-trash"}`}></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {entries.length > 0 && (
            <div className="p-4 text-xs sm:text-sm text-gray-600 border-t border-gray-200">
              {t("common.showingRange", {
                from: 1,
                to: entries.length,
                total: all.length,
                entity: t("language.entity"),
              })}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 flex items-start gap-2">
          <i className="fa-solid fa-circle-info mt-0.5 text-gray-400"></i>
          {t("language.baseNote")}
        </p>
      </main>

      <AddLanguageModal
        open={!!modal}
        language={modal?.code ? { code: modal.code, name: modal.name! } : undefined}
        onClose={() => setModal(null)}
      />
    </div>
  );
};

export default LanguagePage;
